import React, { Fragment, memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { AdminCreateCustomer } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { Customer } from 'src/interfaces/customer'
import { ConfirmCreateCustomerModal } from './ConfirmCreateCustomerModal'
import { CreateCustomerForm } from './create-customer-form/CreateCustomerForm'
import { toCreateCustomerPayload } from './create-customer-form/CreateCustomerForm.mapper'
import type { CreateCustomerFormValues } from './create-customer-form/CreateCustomerForm.interface'
import { GSIcon } from 'src/components/shared/GSIcon'

const FORM_ID = 'create-customer-form'

interface Props {
  onCreated(): void
}

const CreateCustomerButtonComponent = ({ onCreated }: Props) => {
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [pendingValues, setPendingValues] = useState<CreateCustomerFormValues | null>(null)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleOpenForm = useCallback(() => {
    setIsFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false)
  }, [])

  const handleFormSubmit = useCallback((values: CreateCustomerFormValues) => {
    setPendingValues(values)
  }, [])

  const handleCancelConfirm = useCallback(() => {
    setPendingValues(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!pendingValues) {
      return
    }

    setIsSubmitting(true)

    try {
      const payload = await toCreateCustomerPayload(pendingValues)
      const created = await asyncDispatchWithErrorToast<Customer>(
        AdminCreateCustomer.request(payload),
      )

      if (!created) {
        return
      }

      snackbar?.enqueueSnackbar('Customer created', { variant: 'success' })
      setPendingValues(null)
      setIsFormOpen(false)
      onCreated()
    } finally {
      setIsSubmitting(false)
    }
  }, [pendingValues, asyncDispatchWithErrorToast, snackbar, onCreated])

  return (
    <Fragment>
      {/* Page-header CTA in the dashboard's shape (see Opportunities): dark, small, leading
          circle-plus icon. `primary` is reserved for legacy call sites there. */}
      <Button
        size="small"
        color="dark"
        className={tw`px-4`}
        leftIcon={<GSIcon iconName="gs_circle_plus" size={20} css={tw`text-shades-0`} />}
        onClick={handleOpenForm}
        data-testid="create-customer-open-button"
      >
        New customer
      </Button>
      <CommonModal
        isOpen={isFormOpen}
        title="Add a new customer"
        // Wider than the default confirm-dialog width: this modal carries a multi-field form.
        className={tw`max-w-[480px]`}
        onClose={handleCloseForm}
        buttons={
          <Fragment>
            <Button size="small" color="grey" onClick={handleCloseForm}>
              Cancel
            </Button>
            <Button size="small" color="primary" type="submit" form={FORM_ID}>
              Create
            </Button>
          </Fragment>
        }
      >
        <CreateCustomerForm formId={FORM_ID} onSubmit={handleFormSubmit} />
      </CommonModal>
      <ConfirmCreateCustomerModal
        isOpen={Boolean(pendingValues)}
        isSubmitting={isSubmitting}
        values={pendingValues}
        onConfirm={handleConfirm}
        onClose={handleCancelConfirm}
      />
    </Fragment>
  )
}

export const CreateCustomerButton = memo(CreateCustomerButtonComponent)
